import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiPaths } from 'app/modules/utils/apipath.constants';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class FerrerAuthService {
  private _authenticated: boolean = false;
  private _user: any = null;
  private apiUrl = environment.apiUrl;
  constructor(
    private _httpClient: HttpClient,
    private apiService: ApiService
  ) {}
  

  private getAuthId(): string {
    // Try to get from localStorage first
    const storedAuthId = localStorage.getItem('externalAuthId');
    
    console.log('[AuthService] Getting AuthId:', {
      storedAuthId: storedAuthId,
      isEmpty: !storedAuthId,
      allLocalStorage: Object.keys(localStorage)
    });
    
    // Return stored value or fall back to default
    return storedAuthId || '';
  }
  /**
   * Login with email and password
   *
   * @param email
   * @param password
   */
  login(
    email: string,
    password?: string,
    otpCode?: string,
    econsents?: Record< string,boolean >
  ): Observable<any> {
    // Make the actual POST request to your API
    const customHeaders = this.getCustomHeaders();
    const requestBody : {
        email;
        password?: string;
        otpPassword?: string;
        rememberMe: boolean;
        econsents?: Record<string, boolean>;
    } = {
        email,
        rememberMe: true
    };

    if (password !== undefined) {
        requestBody.password = password;
      }
    
    if (otpCode !== undefined) {
        requestBody.otpPassword = otpCode;
      }
    
    if (econsents !== undefined) {
      requestBody.econsents = econsents;
    }
    return this.apiService.post(ApiPaths.auth.login, 
        requestBody, 
        {headers: customHeaders}
    ).pipe(
      tap((response: any) => {
        // Store authentication status and user data
        this._authenticated = true;
        this._user = response.user;
        
        // Store access token in local storage
        if (response.accessToken) {
          localStorage.setItem('accessToken', response.accessToken);
        }
        
        // You may want to store other user info as well
        localStorage.setItem('user', JSON.stringify(this._user));
      }),
      catchError((error) => {
        // Log the error for debugging
        return throwError(() => error);
      })
    );
  }
  

  verifiedEmail(email: string): Observable<any> {
    // Make the actual POST request to your API
    const customHeaders = {
        'Api-Auth-Id': this.getAuthId(),
        'Api-Source-Info': 'eyJFdmVudFNvdXJjZVR5cGUiOiJGYWtlIEV2ZW50U291cmNlVHlwZSIsIkV2ZW50U291cmNlS2V5IjoiRmFrZSBFdmVudFNvdXJjZUtleSIsIkV2ZW50U291cmNlSXRlbSI6IkZha2UgRXZlbnRTb3VyY2VJdGVtIn0='
    }
    return this.apiService.get(ApiPaths.auth.isEmailRegistered(email), 
        undefined,    
        {headers: customHeaders}
    ).pipe(
        map((response: any) => {
        return response;
      }),
      catchError((error) => {
        // Log the error for debugging
        console.error('Authentication error:', error);
        return throwError(() => error);
      })
    );
  }

  sendOtpCode(email: string): Observable<any> {
    const customHeaders = this.getCustomHeaders();
    return this.apiService.get(ApiPaths.auth.sendOtpCode(email), 
        undefined, 
        {headers: customHeaders})
        .pipe(
            map((response: any) => {
                return response;
                }),
                catchError((error) => {
                // Log the error for debugging
                console.error('Authentication error:', error);
                return throwError(() => error);
                })
        );
  }

  sentVerifiedEmail(uuid: string, verificationid: string): Observable<any> {
    const customHeaders = this.getCustomHeaders();
    return this.apiService.post(ApiPaths.auth.gettingActivated(uuid, verificationid), 
        undefined, 
        {headers: customHeaders})
        .pipe(
            map((response: any) => {
                return response;
                }),
                catchError((error) => {
                // Log the error for debugging
                return throwError(() => error);
                })
        );
  }

  rememberPassword(userEmail: string, userPassword: string): Observable<any> {
    const customHeaders = this.getCustomHeaders();
    const userData = {
      email: userEmail,
      password: userPassword,
      repeatPassword: userPassword
    }
    return this.apiService.post(ApiPaths.auth.rememberPassword, 
        userData, 
        {headers: customHeaders})
        .pipe(
            map((response: any) => {
                return response;
                }),
                catchError((error) => {
                // Log the error for debugging
                return throwError(() => error);
                })
        );
  }

  getSessiondata(authId:string): Observable<any> {
    const customHeaders = this.getCustomHeaders();
    return this.apiService.get(ApiPaths.auth.getSessionData, 
        undefined, 
        {headers: customHeaders})
        .pipe(
            map((response: any) => {
                return response;
                }),
                catchError((error) => {
                // Log the error for debugging
                return throwError(() => error);
                })
        );
  }

  getCountryCode(code: string): Observable<any> {
    const customHeaders = this.getCustomHeaders();
    return this.apiService.get(ApiPaths.help.countryConfigByCode(code), 
        undefined, 
        {headers: customHeaders})
        .pipe(
            map((response: any) => {
                return response;
                }),
                catchError((error) => {
                // Log the error for debugging
                return throwError(() => error);
                })
        );
  }
  /**
   * Check if the user is logged in
   */
  get isAuthenticated(): boolean {
    return this._authenticated;
  }
  
  /**
   * Get the logged in user
   */
  get user(): any {
    return this._user;
  }
  
  private getCustomHeaders(): { [key: string]: string } {
    // Base required headers
    const authId = this.getAuthId();
    const headers: { [key: string]: string } = {
      'Content-Type': 'application/json',
      'Api-Auth-Id': authId,
      'Api-Source-Info': 'eyJFdmVudFNvdXJjZVR5cGUiOiJGYWtlIEV2ZW50U291cmNlVHlwZSIsIkV2ZW50U291cmNlS2V5IjoiRmFrZSBFdmVudFNvdXJjZUtleSIsIkV2ZW50U291cmNlSXRlbSI6IkZha2UgRXZlbnRTb3VyY2VJdGVtIn0='
    };
    
    console.log('[AuthService] Custom headers prepared:', {
      'Api-Auth-Id': authId,
      'Api-Auth-Id-Length': authId?.length || 0,
      'Is-Empty': !authId || authId === ''
    });

    // Add optional headers if they exist in localStorage
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) {
      headers['Api-Session-Id'] = sessionId;
    }

    const callback = localStorage.getItem('callback');
    if (callback) {
      headers['Api-Callback-Url'] = callback;
    }

    const product = localStorage.getItem('product');
    if (product) {
      headers['Api-Product-Flag'] = product;
    }

    return headers;
  }

  /**
   * Sign out
   */
  signOut(): Observable<any> {
    // Clear local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    
    // Reset authentication status
    this._authenticated = false;
    this._user = null;
    
    // Return a basic observable that completes immediately
    return of(true);
  }
}